/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-one-expression-per-line */
import React from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useSnackbar } from "notistack";
import { FaRegCalendarAlt, FaUser } from "react-icons/fa";
import { deleteScenarioTotalsAsyncAction } from "app/scenarios/duck/actions";
import buildRequestUrl from "lib/requests/requestUtils";
import { Typography, makeStyles } from "@material-ui/core";
import athenaClient from "lib/axios/clients";
import { useDispatch } from "react-redux";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import mixpanelTrack from "app/common/MixpanelComponent";

const useStyles = makeStyles((theme) => ({
  circularProgress: {
    color: "white",
  },
  scenarioInfoRow: {
    display: "flex",
    alignItems: "center",
    "& :first-child": {
      marginRight: "10px",
    },
  },
  scenarioTitle: {
    marginBottom: "5px",
  },
  dialogTitle: {
    borderBottom: "1px solid #e2e2e2",
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
  },
  dialogActions: {
    borderTop: "1px solid #e2e2e2",
  },
}));

const withDeleteScenario = (WrappedComponent, scenarioProp) => {
  return (props) => {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const dispatch = useDispatch();
    const classes = useStyles();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const handleClickDialogOpen = (e) => {
      e.stopPropagation();
      setDialogOpen(true);
    };

    const handleDialogClose = (e) => {
      e.stopPropagation();
      setDialogOpen(false);
      if (props.onDialogClose) props.onDialogClose(e);
    };

    const loadingCircle = () => (
      <CircularProgress size={24} className={classes.circularProgress} />
    );

    const deleteScenario = (scenario) => {
      const params = {
        path: scenario.id,
      };

      // Request server to delete scenario
      const config = {
        method: "DELETE",
        url: buildRequestUrl(params, "scenarios"),
      };

      return athenaClient(config);
    };

    const handleClickConfirm = (e) => {
      /* MIXPANEL TRACKS */
      mixpanelTrack(`Delete scenario ${scenarioProp.name}`);
      /* END OF MIXPANEL TRACKS */
      handleDialogClose(e);

      const snackbarKey = enqueueSnackbar(
        `Deleting scenario '${scenarioProp.name}'...`,
        {
          persist: true,
          action: loadingCircle,
        }
      );

      deleteScenario(scenarioProp)
        .then(() => {
          dispatch(
            deleteScenarioTotalsAsyncAction({
              scenario: scenarioProp,
            })
          );

          enqueueSnackbar(
            `Scenario '${scenarioProp.name}' was successfully deleted`,
            {
              variant: "success",
            }
          );
        })
        .catch(() => {
          enqueueSnackbar(
            `Error when deleting '${scenarioProp.name}'. Try again shortly`,
            {
              variant: "error",
            }
          );
        })
        .finally(() => {
          closeSnackbar(snackbarKey);
        });
    };

    const handleDialogClick = (e) => {
      /**
       * We need to stop the propagation of the click event when clicking
       * anywhere within the dialog window to prevent side effects
       */
      e.stopPropagation();
    };

    return (
      <>
        <WrappedComponent
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          onClick={handleClickDialogOpen}
          onMouseDown={handleClickDialogOpen}
        />
        {scenarioProp && (
          <Dialog
            open={dialogOpen}
            onClose={handleDialogClose}
            onClick={handleDialogClick}
          >
            <DialogTitle className={classes.dialogTitle}>
              Delete the following scenario?
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" className={classes.scenarioTitle}>
                <b>{scenarioProp.name}</b>
              </Typography>
              <Typography variant="body1" className={classes.scenarioInfoRow}>
                <FaRegCalendarAlt />
                {scenarioProp.cycle.label}
              </Typography>
              <Typography variant="body1" className={classes.scenarioInfoRow}>
                <FaUser />
                {scenarioProp.owner_label}
              </Typography>
              <br />
              <Typography variant="body1">
                This action <b>cannot be undone</b>. Please make sure that this
                is what is intended before proceeding.
              </Typography>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button autoFocus onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button onClick={handleClickConfirm} color="primary">
                Yes
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  };
};

export default withDeleteScenario;
